import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, LayoutList, Table2 } from 'lucide-react';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource } from '../../utils/communityDataSource';
import ThreadPostCard from '../../components/CommunityThread/ThreadPostCard';
import ThreadDetailModal from '../../components/ThreadDetailModal/ThreadDetailModal';

const FEED_MODES = [
  { id: 'feed', label: 'Feed' },
  { id: 'global', label: 'Global' },
  { id: 'trending', label: 'Trending' },
  { id: 'mine', label: 'My threads' },
  { id: 'gps', label: 'GPS only' },
  { id: 'by-tag', label: 'By tag' },
  { id: 'by-board', label: 'By board' },
];

const DEFAULT_BOARD_SEARCH = {
  lat: '37.4979',
  lon: '127.0276',
  radius: '10000',
  planetId: '',
  nameQuery: '',
  boardId: '',
};

const EMPTY = {
  kind: 'general',
  title: '',
  body: '',
  planet_id: '',
  latitude: '',
  longitude: '',
  author_user_id: '1001',
  tags: '',
  board_name: '',
  gps_verified: false,
  arrival_based: false,
};

async function fetchThreadsByMode(mode, params) {
  const { kind, tag, boardId, actingUserId, includeDeleted, limit = 50 } = params;
  switch (mode) {
    case 'global':
      return provider.getThreadsGlobal({ limit });
    case 'trending':
      return provider.getThreadsTrending({ limit });
    case 'mine':
      return provider.getMyThreads({ limit }, Number(actingUserId));
    case 'gps':
      return provider.getThreadsGps({ limit });
    case 'by-tag':
      if (!tag?.trim()) return { data: [], meta: {} };
      return provider.getThreadsByTag({ tag: tag.trim(), limit });
    case 'by-board':
      if (!boardId) return { data: [], meta: {} };
      return provider.getThreadsByBoard(Number(boardId), { limit });
    default:
      return provider.getThreadsFeed({
        kind: kind || undefined,
        include_deleted: includeDeleted,
        limit,
      });
  }
}

export default function CommunityThreads() {
  const [feedMode, setFeedMode] = useState('feed');
  const [kind, setKind] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [boardSearch, setBoardSearch] = useState(DEFAULT_BOARD_SEARCH);
  const [boardSearchSubmitted, setBoardSearchSubmitted] = useState(null);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [viewMode, setViewMode] = useState('feed');
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const queryClient = useQueryClient();
  const dataSource = useCommunityDataSource();

  const queryKey = useMemo(
    () => ['communityThreads', dataSource, { feedMode, kind, tagFilter, includeDeleted, selectedBoardId }],
    [dataSource, feedMode, kind, tagFilter, includeDeleted, selectedBoardId],
  );

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      fetchThreadsByMode(feedMode, {
        kind,
        tag: tagFilter,
        boardId: selectedBoardId,
        actingUserId: form.author_user_id,
        includeDeleted,
        limit: 50,
      }),
    enabled:
      (feedMode !== 'by-board' || !!selectedBoardId) &&
      (feedMode !== 'by-tag' || tagFilter.trim().length > 0),
  });

  const boardsQuery = useQuery({
    queryKey: ['communityBoardSearch', dataSource, boardSearchSubmitted],
    queryFn: () =>
      provider.getBoards({
        latitude: boardSearchSubmitted.lat,
        longitude: boardSearchSubmitted.lon,
        radius_m: boardSearchSubmitted.radius,
        planet_id: boardSearchSubmitted.planetId || undefined,
        limit: 50,
      }),
    enabled: feedMode === 'by-board' && !!boardSearchSubmitted,
  });

  const filteredBoards = useMemo(() => {
    const boards = boardsQuery.data?.data || [];
    const q = boardSearch.nameQuery.trim().toLowerCase();
    if (!q) return boards;
    return boards.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.board_key?.toLowerCase().includes(q),
    );
  }, [boardsQuery.data, boardSearch.nameQuery]);

  const handleBoardSearch = (e) => {
    e.preventDefault();
    setBoardSearchSubmitted({
      lat: Number(boardSearch.lat),
      lon: Number(boardSearch.lon),
      radius: Number(boardSearch.radius) || 10000,
      planetId: boardSearch.planetId,
    });
    setSelectedBoardId('');
  };

  const selectBoardById = (id) => {
    setSelectedBoardId(String(id));
    setFeedMode('by-board');
  };

  const createMut = useMutation({
    mutationFn: () => {
      const tags = form.tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        kind: form.kind,
        body: form.body,
        title: form.title || null,
        planet_id: form.planet_id ? Number(form.planet_id) : null,
        gps_verified: form.gps_verified,
        arrival_based: form.arrival_based,
        tags: tags.length ? tags : undefined,
        board_name: form.board_name || undefined,
      };
      if (form.latitude && form.longitude) {
        payload.latitude = Number(form.latitude);
        payload.longitude = Number(form.longitude);
      }
      return provider.postThread(payload, Number(form.author_user_id));
    },
    onSuccess: () => {
      setModalOpen(false);
      setForm(EMPTY);
      queryClient.invalidateQueries({ queryKey: ['communityThreads'] });
      queryClient.invalidateQueries({ queryKey: ['communityOverview'] });
      queryClient.invalidateQueries({ queryKey: ['communityMapThreads'] });
    },
  });

  const rows = useMemo(() => {
    const allRows = listQuery.data?.data || [];
    if (!search) return allRows;
    const q = search.toLowerCase();
    return allRows.filter(
      (r) =>
        (r.title && r.title.toLowerCase().includes(q)) ||
        (r.body && r.body.toLowerCase().includes(q)) ||
        (Array.isArray(r.tags) && r.tags.some((t) => t.toLowerCase().includes(q))) ||
        (r.board?.name && r.board.name.toLowerCase().includes(q)),
    );
  }, [listQuery.data?.data, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FEED_MODES.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              setFeedMode(id);
              if (id === 'by-board' && !boardSearchSubmitted) {
                setBoardSearchSubmitted({
                  lat: Number(DEFAULT_BOARD_SEARCH.lat),
                  lon: Number(DEFAULT_BOARD_SEARCH.lon),
                  radius: Number(DEFAULT_BOARD_SEARCH.radius) || 10000,
                  planetId: DEFAULT_BOARD_SEARCH.planetId,
                });
              }
            }}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              feedMode === id
                ? 'bg-primary text-black border-primary'
                : 'border-dark-border text-dark-text-secondary hover:border-primary'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        {feedMode === 'feed' && (
          <select
            className="bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">All kinds</option>
            <option value="general">general</option>
            <option value="regional">regional</option>
          </select>
        )}
        {feedMode === 'by-tag' && (
          <>
            <input
              className="bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-40"
              placeholder="Tag prefix (e.g. c)"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value.replace(/\s/g, ''))}
            />
            <span className="text-xs text-dark-text-muted pb-2">
              Prefix match — &quot;c&quot; finds coffee, morning, …
            </span>
          </>
        )}
        {feedMode === 'by-board' && (
          <input
            className="bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-24"
            placeholder="Board ID"
            value={boardSearch.boardId}
            onChange={(e) => setBoardSearch({ ...boardSearch, boardId: e.target.value })}
          />
        )}
        {feedMode === 'by-board' && boardSearch.boardId && (
          <button
            type="button"
            className="px-3 py-2 border border-dark-border rounded text-sm hover:border-primary"
            onClick={() => selectBoardById(boardSearch.boardId)}
          >
            Load board #{boardSearch.boardId}
          </button>
        )}
        <input
          className="bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm min-w-[200px]"
          placeholder="Search title/body/tags"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {feedMode === 'feed' && (
          <label className="flex items-center gap-2 text-sm text-dark-text-secondary">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            Include deleted
          </label>
        )}
        {feedMode === 'trending' && (
          <span className="text-xs text-dark-text-muted">
            Excludes threads with ▼10+ downvotes (mock mirrors API)
          </span>
        )}
        {feedMode === 'by-board' && selectedBoardId && (
          <span className="text-xs text-primary">
            Board #{selectedBoardId} selected
          </span>
        )}
        <div className="flex rounded-lg border border-dark-border overflow-hidden ml-auto">
          <button
            type="button"
            title="Feed view"
            className={`p-2 ${viewMode === 'feed' ? 'bg-primary text-black' : 'text-dark-text-secondary hover:text-primary'}`}
            onClick={() => setViewMode('feed')}
          >
            <LayoutList size={16} />
          </button>
          <button
            type="button"
            title="Table view"
            className={`p-2 ${viewMode === 'table' ? 'bg-primary text-black' : 'text-dark-text-secondary hover:text-primary'}`}
            onClick={() => setViewMode('table')}
          >
            <Table2 size={16} />
          </button>
        </div>
        <button
          type="button"
          className="p-2 border border-dark-border rounded hover:border-primary"
          onClick={() => listQuery.refetch()}
        >
          <RefreshCw size={16} />
        </button>
        <button
          type="button"
          className="flex items-center gap-1 px-3 py-2 bg-primary text-black rounded font-medium text-sm"
          onClick={() => setModalOpen(true)}
        >
          <Plus size={16} /> New thread
        </button>
      </div>

      {feedMode === 'by-board' && (
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 space-y-3">
          <p className="text-xs text-dark-text-muted">
            Search boards by location (GET /boards), then pick one to load its threads (GET
            /boards/&#123;id&#125;/threads).
          </p>
          <form onSubmit={handleBoardSearch} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-dark-text-muted">Latitude</label>
              <input
                className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-32"
                value={boardSearch.lat}
                onChange={(e) => setBoardSearch({ ...boardSearch, lat: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-dark-text-muted">Longitude</label>
              <input
                className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-32"
                value={boardSearch.lon}
                onChange={(e) => setBoardSearch({ ...boardSearch, lon: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-dark-text-muted">Radius (m)</label>
              <input
                className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-24"
                value={boardSearch.radius}
                onChange={(e) => setBoardSearch({ ...boardSearch, radius: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-dark-text-muted">Planet ID</label>
              <input
                className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-20"
                value={boardSearch.planetId}
                onChange={(e) => setBoardSearch({ ...boardSearch, planetId: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-dark-text-muted">Board name filter</label>
              <input
                className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-40"
                placeholder="seoul, feedback…"
                value={boardSearch.nameQuery}
                onChange={(e) => setBoardSearch({ ...boardSearch, nameQuery: e.target.value })}
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-black rounded font-medium text-sm"
            >
              Search boards
            </button>
          </form>

          {boardsQuery.isFetching && (
            <p className="text-xs text-dark-text-muted">Loading boards…</p>
          )}
          {boardsQuery.error && (
            <p className="text-xs text-red-400">{boardsQuery.error.message}</p>
          )}

          {boardSearchSubmitted && !boardsQuery.isFetching && filteredBoards.length === 0 && (
            <p className="text-xs text-dark-text-muted">No boards found in this area.</p>
          )}

          {filteredBoards.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filteredBoards.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => selectBoardById(b.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    String(selectedBoardId) === String(b.id)
                      ? 'bg-primary text-black border-primary'
                      : 'border-dark-border text-dark-text-secondary hover:border-primary hover:text-primary'
                  }`}
                >
                  #{b.name}
                  <span className="ml-1 opacity-70">({b.thread_count} threads)</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {listQuery.isLoading && (
        <p className="text-dark-text-secondary text-sm text-center py-8">Loading feed…</p>
      )}

      {viewMode === 'feed' ? (
        <div className="max-w-xl mx-auto border border-dark-border rounded-xl overflow-hidden bg-dark-card/40 shadow-lg">
          {rows.length === 0 && !listQuery.isLoading && !listQuery.error && (
            <p className="text-center text-dark-text-muted py-12 text-sm">
              {feedMode === 'by-board' && !selectedBoardId
                ? 'Search and select a board to view its threads.'
                : feedMode === 'by-tag' && !tagFilter.trim()
                  ? 'Enter a tag prefix (even one letter) to search.'
                  : 'No threads yet.'}
            </p>
          )}
          {rows.map((thread) => (
            <ThreadPostCard
              key={thread.id}
              thread={thread}
              actingUserId={Number(form.author_user_id)}
              onAdminDetails={setSelectedId}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto border border-dark-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-dark-surface text-dark-text-secondary">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Kind</th>
                <th className="text-left p-2">Author</th>
                <th className="text-left p-2">Title / Body</th>
                <th className="text-left p-2">Board</th>
                <th className="text-left p-2">Tags</th>
                <th className="text-left p-2">Counts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-dark-border hover:bg-dark-surface/50 cursor-pointer"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td className="p-2 text-primary">{row.id}</td>
                  <td className="p-2">{row.kind}</td>
                  <td className="p-2">{row.username || row.author_user_id}</td>
                  <td className="p-2 max-w-xs truncate">{row.title || row.body?.slice(0, 60)}</td>
                  <td className="p-2 text-xs">{row.board?.name || '—'}</td>
                  <td className="p-2 text-xs max-w-[120px] truncate">
                    {(row.tags || []).join(', ') || '—'}
                  </td>
                  <td className="p-2 text-xs text-dark-text-muted whitespace-nowrap">
                    v{row.view_count} ▲{row.up_count ?? 0} ▼{row.down_count ?? 0} c
                    {row.comment_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <ThreadDetailModal
          threadId={selectedId}
          actingUserId={Number(form.author_user_id)}
          onClose={() => setSelectedId(null)}
        />
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-dark-card border border-dark-border rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create thread</h3>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-dark-text-muted">kind</label>
                <select
                  className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  <option value="general">general</option>
                  <option value="regional">regional</option>
                </select>
              </div>
              {['author_user_id', 'title', 'planet_id', 'latitude', 'longitude', 'tags', 'board_name'].map(
                (field) => (
                  <div key={field}>
                    <label className="text-dark-text-muted">
                      {field}
                      {field === 'tags' && ' (comma-separated, max 5)'}
                    </label>
                    <input
                      className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2"
                      value={form[field]}
                      placeholder={field === 'tags' ? 'coffee, morning' : field === 'board_name' ? 'seoul-cafes' : ''}
                      onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    />
                  </div>
                ),
              )}
              <div>
                <label className="text-dark-text-muted">body</label>
                <textarea
                  className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2"
                  rows={3}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.gps_verified}
                  onChange={(e) => setForm({ ...form, gps_verified: e.target.checked })}
                />
                gps_verified
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.arrival_based}
                  onChange={(e) => setForm({ ...form, arrival_based: e.target.checked })}
                />
                arrival_based
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-4 py-2 border border-dark-border rounded"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-primary text-black rounded font-medium"
                onClick={() => createMut.mutate()}
                disabled={createMut.isPending}
              >
                Create
              </button>
            </div>
            {createMut.error && (
              <p className="text-red-400 text-sm mt-2">{createMut.error.message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
